import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { userTypes } from 'constants/user'
import { emitSpeakerVolume } from 'helpers/events'
import useInterval from 'hooks/useInterval/useInterval'
import useIsTrackEnabled from 'hooks/useIsTrackEnabled/useIsTrackEnabled'
import useVideoContext from 'hooks/useVideoContext/useVideoContext'
import { handleSetIsSpeaking } from 'store/actions/Room'
import { isDesktop, isIPad } from 'utils/device'

import { AudioOff } from './styles'

const AudioContext = window.AudioContext || window.webkitAudioContext
const AUDIO_INDICATOR_INTERVAL = 500
let audioContext

export function initializeAnalyser(stream) {
    audioContext = audioContext || new AudioContext()
    const audioSource = audioContext.createMediaStreamSource(stream)

    const analyser = audioContext.createAnalyser()
    analyser.smoothingTimeConstant = 0.4
    analyser.fftSize = 512

    audioSource.connect(analyser)
    return analyser
}

function AudioLevelIndicator({ isLocal, audioTrack, iconWidth, identity }) {
    const mediaStreamRef = useRef()
    const isTrackEnabled = useIsTrackEnabled(audioTrack)
    const { userType } = useVideoContext()

    const dispatch = useDispatch()

    const isTeacher = userType === userTypes.TEACHER

    const [analyser, setAnalyser] = useState()
    const [sampleArray, setSampleArray] = useState(null)

    const reinitializeAnalyser = () => {
        //handleStopped()
        mediaStreamRef.current = new MediaStream([audioTrack.mediaStreamTrack])
        setAnalyser(initializeAnalyser(mediaStreamRef.current))
    }

    useEffect(() => {
        if (audioTrack && isTrackEnabled) {
            // Here we create a new MediaStream from a clone of the mediaStreamTrack.
            // A clone is created to allow multiple instances of this component for a single
            // AudioTrack on iOS Safari. It is stored in a ref so that the cloned track can be stopped
            // when the original track is stopped.
            // the audio in ipad after muting and unmuting wasnt coming hence have used clone so that it can replicate the track here and get audio after unmuting
            mediaStreamRef.current = new MediaStream([
                isIPad
                    ? audioTrack.mediaStreamTrack.clone()
                    : audioTrack.mediaStreamTrack,
            ])

            setAnalyser(initializeAnalyser(mediaStreamRef.current))

            // Here we reinitialize the AnalyserNode on focus to avoid an issue in Safari
            // where the analysers stop functioning when the user switches to a new tab
            // and switches back to the app.
            window.addEventListener('focus', reinitializeAnalyser)

            return () => {
                window.removeEventListener('focus', reinitializeAnalyser)
            }
        } else {
            isLocal && dispatch(handleSetIsSpeaking({ isSpeaking: false }))
            const shouldDispatch = identity?.uuid && isDesktop
            shouldDispatch && emitSpeakerVolume(identity.uuid, 0)
        }
    }, [audioTrack ? audioTrack.mediaStreamTrack : audioTrack, isTrackEnabled])

    const audioIndicatorCb = () => {
        if (sampleArray) {
            analyser.getByteFrequencyData(sampleArray)
            let values = 0
            const length = sampleArray.length
            for (let i = 0; i < length; i++) {
                values += sampleArray[i]
            }

            const volume = Math.min(
                21,
                Math.max(0, Math.log10(values / length / 3) * 14)
            )

            isLocal && dispatch(handleSetIsSpeaking({ volume }))
            const shouldDispatch = identity?.uuid && isDesktop
            shouldDispatch && emitSpeakerVolume(identity.uuid, volume)
        }
    }

    useInterval(
        audioIndicatorCb,
        isTrackEnabled && analyser ? AUDIO_INDICATOR_INTERVAL : null
    )

    useEffect(() => {
        if (isTrackEnabled && analyser) {
            setSampleArray(new Uint8Array(analyser.frequencyBinCount))
        }
    }, [isTrackEnabled, analyser])

    console.count('AudioLevelIndicator Component')

    return !isTrackEnabled ? (
        <AudioOff
            width={iconWidth}
            src="/assets/revamp/mic-off-tile.svg"
            alt="micoff"
            isTeacher={isTeacher}
            isLocal={isLocal}
        />
    ) : null
}

export default AudioLevelIndicator
